import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image } from 'react-bootstrap';
import { GeoAlt, BoxArrowUpRight, ChevronDown, Check2 } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import useUpcomingElections from '../../hooks/useUpcomingElections';
import { formatElectionDate, fetchStateCounties } from '../../utils/electionsApi';
import {
  useHomeLocation,
  hasDistricts,
  bareCountyName,
  STATE_OPTIONS,
} from '../../utils/homeLocation';
import { fetchApi2 } from '../../utils/api2';
import { normalizeDistrict } from '../../utils/ballot';
import LocalCandidates from '../members/LocalCandidates';
import StateCandidates from '../members/StateCandidates';
import CandidatesLinkButton from '../members/CandidatesLinkButton';
import LocationPicker from '../pages/LocationPicker';
import PartiesOnBallot from './parties/PartiesOnBallot';
import MyBallot from './MyBallot';
import './UpcomingElections.css';

// The elections panel at the top of the Elections topic on USHomePage.
//
// Three things stacked, and they answer different questions:
//
//   when    /elections/dates -- one line, the next election in this state.
//   which   the parties on that ballot, each a way into its party page.
//   who     the candidates, by level.
//
// The parties sit between the date and the candidates because a lot of
// readers arrive party first: they know which party they want to read about
// before they know a single name on the ballot. See PartiesOnBallot. It fails
// on its own, like the date line, so an unanswered ballot costs the strip and
// nothing else.
//
// Below federal the candidate list is LocalCandidates rather than a second one
// built here. It already owns paging, the truncation notice, the no-bulk-list
// empty state that points at the state's own election office, and navigation to
// /us/candidate/local/:stateAbbr/:level/:ref. A parallel implementation would
// have drifted from it the first time either changed.
//
// Federal is StateCandidates, for the same reason. Those candidates come from
// the other API (api.govglance.org, bearer token, FEC candidate IDs) and have a
// different shape, so the two lists are not merged -- they are two components
// behind one row of tabs.
//
// There is deliberately no date picker. Election dates are federal and
// statewide -- the November general is derived from statute -- so a row of date
// pills sitting above a county candidate list was offering a choice that does
// not narrow anything below it. The next date is stated once and left alone.
//
// On desktop the panel is capped at the same 700px column the Spending page and
// the member pages use (.member-page-content), so it reads as a card rather than
// a full-width bar. See UpcomingElections.css.
//
// ─── Where the county list comes from ─────────────────────────────────────────
//
// fetchStateCounties, not the candidate rows. LocalCandidates reports the
// counties it found via onFacets, and building the picker from that makes the
// picker a reflection of whatever happened to load: this panel pre-applies the
// reader's own county, so the first request is already filtered, the rows come
// back carrying one county, and the only option offered is the county already
// selected. The agency roster from /elections/offices does not move when the
// candidate query does. The facets remain as a fallback for a state whose
// agencies could not be read.
//
// ─── District dropdowns ───────────────────────────────────────────────────────
//
// Federal and State are drawn by district, so on those levels a district
// dropdown sits beside the state picker: one for the congressional district on
// Federal, and one per legislative chamber on State (a place is in a different
// district in each chamber, so one list would be ambiguous -- Iowa senate 50 and
// house 50 are different places). Nebraska's single chamber gets one.
//
// "All" is the whole state's lists, the same StateCandidates and LocalCandidates
// views as before. A district narrows to MyBallot: the contests for that
// district, plus the U.S. Senate race on Federal and the statewide offices on
// State, which are on every ballot in the state.
//
// At home the dropdowns start on the districts the reader saved -- the same
// record the Representatives page reads and LocationPicker writes. On another
// state they start on All. Picking a district only browses: it is never saved,
// for the same reason the state dropdown never is. "Select your district" is
// still how somebody saves their own.
//
// The district lists come from /districts/state/{state}, in the API's natural
// order (2 before 10, 27 before 27A), cached per state for the session.
//
// ─── Two kinds of "where" ─────────────────────────────────────────────────────
//
// The state dropdown browses; it does not move house. Changing it never writes
// to storage, so looking up Ohio does not rewrite homeState, discard the
// districts the reader resolved from their address, or follow them onto the
// Representatives page. The saved location is the default and the reset target;
// LocationPicker is still how somebody changes where they live.

const CANDIDATE_LIST_SIZE = 5;

// 'federal' is served by StateCandidates; the other three are levels api2
// accepts. api2's 'local' (county and city together) is left out of the tabs:
// it overlaps the two beside it, so as another button it reads as a place
// rather than as a union of the other two.
const LEVELS = ['federal', 'state', 'county', 'city'];

// Levels with no county dimension. api2 rejects level=state with a county
// outright, and a congressional or Senate race is not filtered by county at all.
const COUNTYLESS_LEVELS = new Set(['federal', 'state']);

// FEC office types StateCandidates accepts. President is left out: it is not a
// state race, and it is on the ballot one year in four.
const CHAMBERS = ['House', 'Senate'];

const NO_PICKS = { congressional: '', upper: '', lower: '' };

// Touch devices get no auto-focus. Focusing an input opens the on-screen
// keyboard over the list, and iOS Safari zooms into any focused input under
// 16px -- tapping the state picker zoomed the page. Read at open time rather
// than at module load so a hybrid device that attaches a mouse is picked up.
const hasFinePointer = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: fine)').matches;

// ─── District lists ───────────────────────────────────────────────────────────
//
// /districts/state/{state} returns federal.congressional.districts as one flat
// list and state_legislature.chambers with one entry per chamber, each with its
// own districts list. Field names are read defensively: the value is the
// district as the saved location stores it, the label is the state's own
// wording where the entry carries one.

const districtCache = new Map();

const districtValue = (entry) =>
  String(entry?.district ?? entry?.number ?? entry?.label ?? entry?.name ?? '').trim();

const districtLabel = (entry) =>
  String(entry?.label ?? entry?.name ?? districtValue(entry)).trim();

const toDistrictList = (entries) => {
  const seen = new Set();
  return (Array.isArray(entries) ? entries : [])
    .map((entry) => ({ value: districtValue(entry), label: districtLabel(entry) }))
    .filter((entry) => {
      const key = normalizeDistrict(entry.value);
      if (!entry.value || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

// Which saved slot a chamber fills. Nebraska's one chamber is its Legislature,
// which the saved location records as the upper seat, so a lone chamber is
// upper whatever it is called.
const chamberSlot = (chamber, total) => {
  if (total === 1) return 'upper';
  const words = `${chamber?.chamber || ''} ${chamber?.type || ''} ${chamber?.key || ''} ${
    chamber?.name || ''
  }`.toLowerCase();
  if (/upper|senate/.test(words)) return 'upper';
  if (/lower|house|assembly|delegates/.test(words)) return 'lower';
  return null;
};

const readStateDistricts = (payload) => {
  const congressional = toDistrictList(payload?.federal?.congressional?.districts);

  const rawChambers = Array.isArray(payload?.state_legislature?.chambers)
    ? payload.state_legislature.chambers
    : [];

  const chambers = {};
  rawChambers.forEach((chamber) => {
    const slot = chamberSlot(chamber, rawChambers.length);
    if (!slot || chambers[slot]) return;
    chambers[slot] = {
      name: chamber?.name || chamber?.label || '',
      districts: toDistrictList(chamber?.districts),
    };
  });

  return { congressional, upper: chambers.upper || null, lower: chambers.lower || null };
};

const useStateDistricts = (stateAbbr) => {
  const [districts, setDistricts] = useState(() =>
    stateAbbr ? districtCache.get(stateAbbr) || null : null
  );

  useEffect(() => {
    if (!stateAbbr) {
      setDistricts(null);
      return undefined;
    }

    const cached = districtCache.get(stateAbbr);
    if (cached) {
      setDistricts(cached);
      return undefined;
    }

    // Cleared rather than left on the previous state's districts, which would
    // be offered under the new state's name for as long as the request takes.
    setDistricts(null);
    let cancelled = false;

    fetchApi2(`/districts/state/${encodeURIComponent(stateAbbr)}`, {})
      .then((payload) => {
        const parsed = readStateDistricts(payload);
        districtCache.set(stateAbbr, parsed);
        if (!cancelled) setDistricts(parsed);
      })
      .catch(() => {
        // No lists means no dropdowns, and the panel stays on the whole state.
        if (!cancelled) setDistricts(null);
      });

    return () => {
      cancelled = true;
    };
  }, [stateAbbr]);

  return districts;
};

// The option a saved district corresponds to, compared in normalized form so
// "05", "5" and "District 5" all land on the same row. Empty when the state's
// list does not contain it.
const snapToOption = (saved, options) => {
  if (!saved || !options?.length) return '';
  const wanted = normalizeDistrict(saved);
  const match = options.find((option) => normalizeDistrict(option.value) === wanted);
  return match ? match.value : '';
};

const sameDistrict = (a, b) => Boolean(a && b) && normalizeDistrict(a) === normalizeDistrict(b);

// ─── Pill select ──────────────────────────────────────────────────────────────
// A custom listbox rather than <select>, for the reason LocationPicker gives:
// native selects open an OS picker that ignores every font rule the app sets.
// Shared by the state, district and county pickers so they look like one
// control repeated rather than several that happen to sit together.
//
// The county list runs to 254 rows in Texas, so this one filters.

const PillSelect = ({ value, options, onChange, placeholder, clearLabel, ariaLabel }) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const wrapperRef = useRef(null);
  const filterRef = useRef(null);
  const listRef = useRef(null);
  const selectedRef = useRef(null);

  const selected = useMemo(
    () => options.find((option) => option.value === value) || null,
    [options, value]
  );

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, filter]);

  // This one floats over the page rather than living in a modal, so a click
  // elsewhere should close it.
  useEffect(() => {
    if (!open) {
      setFilter('');
      return undefined;
    }

    const onDocumentClick = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [open]);

  // Open at the current selection rather than dropping the reader at the top of
  // a long list. With a mouse and keyboard, a long list focuses its filter
  // instead, since typing is faster than scrolling. On touch it never does --
  // see hasFinePointer.
  //
  // The list is scrolled by hand rather than with scrollIntoView, which scrolls
  // every scrollable ancestor to bring the row into view -- including the page,
  // which on a phone jumps the whole screen out from under the reader's thumb.
  useEffect(() => {
    if (!open) return;

    if (options.length > 12 && hasFinePointer()) {
      filterRef.current?.focus();
      return;
    }

    const list = listRef.current;
    const item = selectedRef.current;
    if (!list || !item) return;

    const listBox = list.getBoundingClientRect();
    const itemBox = item.getBoundingClientRect();
    list.scrollTop += itemBox.top - listBox.top - (list.clientHeight - item.clientHeight) / 2;
  }, [open, options.length]);

  return (
    <div className="ue-select" ref={wrapperRef}>
      <button
        type="button"
        className="ue-select-toggle"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((current) => !current)}
      >
        <GeoAlt size={12} className="me-1" />
        <span className={selected ? '' : 'ue-select-placeholder'}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={12} className="ms-1" />
      </button>

      {open && (
        <div className="ue-select-menu">
          {options.length > 12 && (
            <input
              ref={filterRef}
              type="text"
              className="ue-select-filter"
              value={filter}
              placeholder={t('common:filter', 'Filter')}
              onChange={(e) => setFilter(e.target.value)}
            />
          )}

          <ul role="listbox" className="ue-select-list" ref={listRef}>
            {clearLabel && value && (
              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="ue-select-option ue-select-clear"
                  onClick={() => {
                    onChange('');
                    setOpen(false);
                  }}
                >
                  {clearLabel}
                </button>
              </li>
            )}

            {visible.map((option) => {
              const isSelected = option.value === value;
              return (
                <li key={option.value}>
                  <button
                    type="button"
                    ref={isSelected ? selectedRef : null}
                    role="option"
                    aria-selected={isSelected}
                    className={`ue-select-option ${isSelected ? 'ue-select-option-selected' : ''}`}
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    <span className="ue-select-option-label">
                      {/* Fixed slot: DC and the territories have no flag asset,
                          and counties and districts never have one, so without
                          reserved space their names sit out of line with
                          everything else. */}
                      <span className="ue-select-flag-slot">
                        {option.flag && (
                          <Image
                            src={option.flag}
                            alt=""
                            className="ue-select-flag"
                            onError={(e) => {
                              e.currentTarget.style.visibility = 'hidden';
                            }}
                          />
                        )}
                      </span>
                      <span>{option.label}</span>
                    </span>
                    {isSelected && <Check2 size={15} />}
                  </button>
                </li>
              );
            })}

            {!visible.length && (
              <li className="ue-select-empty">{t('common:noResultsFound', 'No results found.')}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

// ─── Panel ────────────────────────────────────────────────────────────────────

const UpcomingElections = () => {
  const { t } = useTranslation('common');
  const { location, saveLocation } = useHomeLocation();

  // null means "wherever I live". Held rather than defaulted to the home
  // abbreviation so the reset below can tell browsing from being home, and so
  // saving a new home location is picked up rather than pinned to the old one.
  const [viewState, setViewState] = useState(null);
  // Federal first: the House and Senate races are the ones most readers come
  // looking for, and they are the same for everyone in the state, so the panel
  // has something to show before a county has been resolved.
  const [level, setLevel] = useState('federal');
  const [chamber, setChamber] = useState('House');
  const [county, setCounty] = useState('');
  const [stateCounties, setStateCounties] = useState([]);
  const [facetCounties, setFacetCounties] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  // The district each dropdown is on. '' is All.
  const [picks, setPicks] = useState(NO_PICKS);

  const homeAbbr = location?.stateAbbr || null;
  const activeAbbr = viewState || homeAbbr;
  const viewingHome = Boolean(activeAbbr) && activeAbbr === homeAbbr;
  const isFederal = level === 'federal';
  const isStateLevel = level === 'state';

  const homeState = useMemo(
    () => STATE_OPTIONS.find((option) => option.abbr === homeAbbr) || null,
    [homeAbbr]
  );

  const activeState = useMemo(
    () => STATE_OPTIONS.find((option) => option.abbr === activeAbbr) || null,
    [activeAbbr]
  );

  // Dates only. The ballot is not fetched here: the candidate list below is
  // filtered by level rather than by date. PartiesOnBallot reads the ballot for
  // itself, through a cache it shares with the party page.
  const { next, notes, loading, error, authority } = useUpcomingElections({
    stateAbbr: activeAbbr,
    withBallot: false,
  });

  const stateDistricts = useStateDistricts(activeAbbr);

  // ─── District options ───
  //
  // A bare number gets a word in front so the closed pill still says what it
  // is ("District 5", "Senate 12"); a label that already has words of its own
  // ("Belknap 1") is left as the state wrote it.
  const withPrefix = useCallback(
    (label, prefix) => (/^\d+[A-Z]?$/i.test(label) ? `${prefix} ${label}` : label),
    []
  );

  const upperName = stateDistricts?.upper?.name || t('common:stateSenate', 'State Senate');
  const lowerName = stateDistricts?.lower?.name || t('common:stateHouse', 'State House');

  const congressionalOptions = useMemo(
    () =>
      (stateDistricts?.congressional || []).map((entry) => ({
        value: entry.value,
        label: withPrefix(entry.label, t('common:district', 'District')),
      })),
    [stateDistricts, withPrefix, t]
  );

  const upperOptions = useMemo(
    () =>
      (stateDistricts?.upper?.districts || []).map((entry) => ({
        value: entry.value,
        label: withPrefix(entry.label, upperName),
      })),
    [stateDistricts, withPrefix, upperName]
  );

  const lowerOptions = useMemo(
    () =>
      (stateDistricts?.lower?.districts || []).map((entry) => ({
        value: entry.value,
        label: withPrefix(entry.label, lowerName),
      })),
    [stateDistricts, withPrefix, lowerName]
  );

  // A dropdown with one district would offer that district or All, which are
  // the same races -- an at-large state's House seat. It is left out.
  const showCongressionalPicker = isFederal && congressionalOptions.length > 1;
  const showUpperPicker = isStateLevel && upperOptions.length > 1;
  const showLowerPicker = isStateLevel && lowerOptions.length > 1;

  // Start on the saved districts at home and on All anywhere else, and again
  // whenever the state, the saved districts, or the district lists change. A
  // saved district the state's list does not contain falls back to All rather
  // than a pill showing a placeholder over a filter that is quietly applied.
  useEffect(() => {
    if (!viewingHome) {
      setPicks(NO_PICKS);
      return;
    }
    setPicks({
      congressional: snapToOption(location?.congressionalDistrict, congressionalOptions),
      upper: snapToOption(location?.stateUpperDistrict, upperOptions),
      lower: snapToOption(location?.stateLowerDistrict, lowerOptions),
    });
  }, [
    activeAbbr,
    viewingHome,
    location?.congressionalDistrict,
    location?.stateUpperDistrict,
    location?.stateLowerDistrict,
    congressionalOptions,
    upperOptions,
    lowerOptions,
  ]);

  const setPick = (slot) => (value) => setPicks((current) => ({ ...current, [slot]: value }));

  // A district narrows to MyBallot; All on every dropdown of this level is the
  // whole state's lists.
  const showBallot = isFederal
    ? showCongressionalPicker && Boolean(picks.congressional)
    : isStateLevel && ((showUpperPicker && picks.upper) || (showLowerPicker && picks.lower));

  // What MyBallot matches against: the picked districts, not the saved ones.
  // The saved labels ("Indiana House District 24") are kept only where the
  // pick is the reader's own district, since on any other they would name the
  // wrong seat. A chamber left on All is passed as no district, and MyBallot
  // shows nothing for it rather than asking the reader to pick one.
  const ballotLocation = useMemo(() => {
    const own = (slot, savedField) => viewingHome && sameDistrict(picks[slot], location?.[savedField]);

    return {
      stateAbbr: activeAbbr,
      congressionalDistrict: picks.congressional || null,
      congressionalLabel: own('congressional', 'congressionalDistrict')
        ? location?.congressionalLabel || null
        : null,
      stateUpperDistrict: picks.upper || null,
      stateUpperLabel: own('upper', 'stateUpperDistrict') ? location?.stateUpperLabel || null : null,
      stateUpperChamberName: stateDistricts?.upper?.name || location?.stateUpperChamberName || null,
      stateLowerDistrict: picks.lower || null,
      stateLowerLabel: own('lower', 'stateLowerDistrict') ? location?.stateLowerLabel || null : null,
      stateLowerChamberName: stateDistricts?.lower?.name || location?.stateLowerChamberName || null,
    };
  }, [activeAbbr, picks, viewingHome, location, stateDistricts]);

  // The county roster for the picker, independent of the candidate query. See
  // the note at the top of the file for why this is not derived from the rows.
  useEffect(() => {
    if (!activeAbbr) {
      setStateCounties([]);
      return undefined;
    }

    const controller = new AbortController();

    fetchStateCounties(activeAbbr, { signal: controller.signal })
      .then((names) => {
        if (!controller.signal.aborted) setStateCounties(names);
      })
      .catch(() => {
        // fetchStateCounties already swallows its own failures and warns; this
        // only catches the abort.
      });

    return () => controller.abort();
  }, [activeAbbr]);

  // A county belongs to a state, and neither a statewide nor a federal contest
  // is a county contest. So the filter resets on either change, and below those
  // levels falls back to the reader's own county, which the app already
  // resolved from their address and never asked them to type.
  useEffect(() => {
    if (COUNTYLESS_LEVELS.has(level)) {
      setCounty('');
      return;
    }
    setCounty(viewingHome ? location?.county || '' : '');
  }, [activeAbbr, level, viewingHome, location?.county]);

  // LocalCandidates lists this in an effect's dependencies, so it has to be
  // stable, and the new array it hands over has to be compared rather than
  // stored blindly -- setting state with an equal-but-new array on every fetch
  // would re-render forever.
  const handleFacets = useCallback(({ counties }) => {
    setFacetCounties((current) => {
      const incoming = Array.isArray(counties) ? counties : [];
      if (
        current.length === incoming.length &&
        current.every((value, index) => value === incoming[index])
      ) {
        return current;
      }
      return incoming;
    });
  }, []);

  // The agency roster where it could be read, the rows' own counties otherwise.
  const countyNames = stateCounties.length ? stateCounties : facetCounties;

  // The saved county and the published one are rarely spelled the same way:
  // "Hamilton County" against "Hamilton". api2 matches either, but the picker
  // would show a placeholder while a filter was quietly applied, so the value
  // is snapped to whichever spelling the list actually uses.
  useEffect(() => {
    if (!county || !countyNames.length) return;
    if (countyNames.includes(county)) return;

    const match = countyNames.find(
      (candidate) => bareCountyName(candidate) === bareCountyName(county)
    );
    if (match) setCounty(match);
  }, [county, countyNames]);

  const stateOptions = useMemo(
    () =>
      STATE_OPTIONS.map((option) => ({
        value: option.abbr,
        label: option.name,
        flag: option.flag,
      })),
    []
  );

  const countyOptions = useMemo(
    () => countyNames.map((name) => ({ value: name, label: name })),
    [countyNames]
  );

  // Only offered where it means something: statewide and federal contests have
  // no county, and a state whose counties could not be read at all would get an
  // empty picker rather than a useless one.
  const showCountyPicker = !COUNTYLESS_LEVELS.has(level) && countyOptions.length > 0;

  const levelLabel = (key) =>
    t(`common:${key}`, key.charAt(0).toUpperCase() + key.slice(1));

  const chamberLabel = (key) =>
    key === 'House' ? t('common:usHouse', 'U.S. House') : t('common:usSenate', 'U.S. Senate');

  const allLabel = t('common:all', 'All');

  return (
    <div className="upcoming-elections">
      {/* A plain heading: the panel is the whole Elections page now, so there
          is nothing to minimize it in favour of. */}
      <h2 className="ue-title">{t('common:upcomingElections', 'Upcoming elections')}</h2>

      <div className="ue-controls">
        <PillSelect
          value={activeAbbr}
          options={stateOptions}
          onChange={setViewState}
          placeholder={t('common:chooseState', 'Choose a state')}
          ariaLabel={t('common:state', 'State')}
        />

        {showCongressionalPicker && (
          <PillSelect
            value={picks.congressional}
            options={congressionalOptions}
            onChange={setPick('congressional')}
            placeholder={t('common:allDistricts', 'All districts')}
            clearLabel={allLabel}
            ariaLabel={t('common:usCongressionalDistrict', 'U.S. congressional district')}
          />
        )}

        {showUpperPicker && (
          <PillSelect
            value={picks.upper}
            options={upperOptions}
            onChange={setPick('upper')}
            placeholder={t('common:allChamberDistricts', 'All {{chamber}}', { chamber: upperName })}
            clearLabel={allLabel}
            ariaLabel={upperName}
          />
        )}

        {showLowerPicker && (
          <PillSelect
            value={picks.lower}
            options={lowerOptions}
            onChange={setPick('lower')}
            placeholder={t('common:allChamberDistricts', 'All {{chamber}}', { chamber: lowerName })}
            clearLabel={allLabel}
            ariaLabel={lowerName}
          />
        )}

        {showCountyPicker && (
          <PillSelect
            value={county}
            options={countyOptions}
            onChange={setCounty}
            placeholder={t('common:allCounties', 'All counties')}
            clearLabel={t('common:allCounties', 'All counties')}
            ariaLabel={t('common:county', 'County')}
          />
        )}

        {homeAbbr && !viewingHome && (
          <button type="button" className="ue-reset" onClick={() => setViewState(null)}>
            {t('common:backToMyState', 'Back to {{state}}', {
              state: homeState?.name || homeAbbr,
            })}
          </button>
        )}
      </div>

      {/* ─── When ─── */}
      {/* One line, and it fails on its own: the candidate list below does
          not depend on it, so a dead /elections/dates costs the date and
          nothing else. */}
      {activeAbbr && !loading && !error && next && (
        <div className="ue-next">
          {/* No label for the reader's own state: the panel is headed
              "Upcoming elections", so a date directly under it already reads
              as the next election. Another state's date still names the
              state, since that is the one thing the heading doesn't say. */}
          {!viewingHome && (
            <span className="ue-next-label">
              {t('common:nextElectionIn', 'Next election in {{state}}', {
                state: activeState?.name || activeAbbr,
              })}
            </span>
          )}
          <span className="ue-next-date">{formatElectionDate(next.date)}</span>
          {next.daysUntil === 0 && (
            <span className="ue-next-countdown">{t('common:electionToday', 'Today')}</span>
          )}
          {next.daysUntil > 0 && (
            <span className="ue-next-countdown">
              {t('common:electionInDays', 'In {{count}} days', { count: next.daysUntil })}
            </span>
          )}
        </div>
      )}

      {/* Every state should have a next election even in a quiet year, so
          an empty answer means the question went unanswered rather than
          answered with "none". The state's own office is offered instead of
          claiming there is nothing coming. */}
      {activeAbbr && !loading && !error && !next && authority && (
        <a
          className="ue-authority"
          href={authority.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {authority.agencyName
            ? t('common:checkAuthority', 'Check {{agency}}', { agency: authority.agencyName })
            : t('common:checkStateElectionOffice', 'Check the state election office')}
          <BoxArrowUpRight className="ms-2" size={12} />
        </a>
      )}

      {/* ─── Which ─── */}
      {/* Keyed on the state so a browse to another state shows skeleton
          chips rather than the previous state's parties for a moment. */}
      {activeAbbr && <PartiesOnBallot key={activeAbbr} stateAbbr={activeAbbr} showHint />}

      {/* ─── Who ─── */}
      {!activeAbbr ? (
        <div className="ue-prompt">
          <p className="ue-prompt-text">
            {t(
              'common:electionsChooseState',
              'Choose a state to see who is running, or set your location for your own districts.'
            )}
          </p>
          <button type="button" className="ue-nudge" onClick={() => setShowPicker(true)}>
            <GeoAlt size={13} className="me-1" />
            {t('common:setLocation', 'Set location')}
          </button>
        </div>
      ) : (
        <>
          <div className="ue-levels" role="tablist">
            {LEVELS.map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={level === key}
                className={`ue-level ${level === key ? 'ue-level-active' : ''}`}
                onClick={() => setLevel(key)}
              >
                {levelLabel(key)}
              </button>
            ))}
          </div>

          {showBallot ? (
            <MyBallot
              key={`${activeAbbr}-${level}-${picks.congressional}-${picks.upper}-${picks.lower}-${
                next?.date || ''
              }`}
              stateAbbr={activeAbbr}
              date={next?.date || null}
              level={level}
              location={ballotLocation}
              onShowAll={() =>
                setPicks((current) =>
                  isFederal ? { ...current, congressional: '' } : { ...current, upper: '', lower: '' }
                )
              }
            />
          ) : isFederal ? (
            <>
              {/* A second row rather than House and Senate as top-level
                  tabs: they are two offices within one level, and six tabs
                  in a row does not fit a phone. */}
              <div className="ue-levels ue-levels-sub" role="tablist">
                {CHAMBERS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={chamber === key}
                    className={`ue-level ${chamber === key ? 'ue-level-active' : ''}`}
                    onClick={() => setChamber(key)}
                  >
                    {chamberLabel(key)}
                  </button>
                ))}
              </div>

              <StateCandidates
                // Remounts on a change for the same reason as below, and
                // so the "More" count starts over rather than carrying from
                // one chamber into the other.
                key={`${activeAbbr}-${chamber}`}
                stateAbbreviation={activeAbbr}
                candidateType={chamber}
                // No filter input on the home panel: five rows do not need
                // one, the full page behind View all has it, and it would be
                // a second input for iOS to zoom into.
                showSearch={false}
                initialCount={CANDIDATE_LIST_SIZE}
              />
            </>
          ) : (
            <LocalCandidates
              // Remounts on a filter change rather than briefly showing the
              // previous selection's rows under the new one.
              key={`${activeAbbr}-${level}-${county}`}
              stateAbbr={activeAbbr}
              level={level}
              county={county}
              initialCount={CANDIDATE_LIST_SIZE}
              onFacets={handleFacets}
              // Decides whether the truncation notice can tell the reader to
              // narrow by county, or would be naming a control that is not on
              // screen.
              countyFilterAvailable={showCountyPicker}
            />
          )}

          <div className="ue-footer">
            {/* Districts are drawn by block. Offered only at home: on
                another state there are no districts of theirs to resolve,
                and the picker saves over where they live. */}
            {viewingHome && !hasDistricts(location) && (
              <button type="button" className="ue-nudge" onClick={() => setShowPicker(true)}>
                <GeoAlt size={13} className="me-1" />
                {t('common:selectDistrict', 'Select your district')}
              </button>
            )}
          </div>
        </>
      )}

      {/* api2 states what a response does not cover rather than leaving a
          short list to be read as a complete one -- municipal contests have
          no national source of dates, and state primary dates are not
          collected everywhere. Passed through verbatim. */}
      {notes && (
        <div className="ue-notes">
          <p>{notes}</p>
        </div>
      )}

      <LocationPicker
        show={showPicker}
        onHide={() => setShowPicker(false)}
        currentLocation={location}
        onSave={saveLocation}
      />
    </div>
  );
};

export default UpcomingElections;